import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Spinner } from 'react-bootstrap';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Footer from './Footer';
import jobListings from '../../data/jobListings';

const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(true);
  const formRef = useRef(null);
  
  useEffect(() => {
    // Find the job listing with matching id
    const jobDetail = jobListings.find(j => j.id === jobId);
    
    // If job not found, navigate back to careers page
    if (!jobDetail) {
      navigate('/careers');
      return;
    }
    
    setJob(jobDetail);
    
    // Load Tally embed script
    const existingScript = document.querySelector('script[src="https://tally.so/widgets/embed.js"]');
    
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://tally.so/widgets/embed.js';
      script.async = true;
      script.id = 'tally-script';
      document.body.appendChild(script);
      
      script.onload = () => {
        if (window.Tally && window.Tally.loadEmbeds) {
          window.Tally.loadEmbeds();
        }
      };
    } else if (window.Tally && window.Tally.loadEmbeds) {
      // If script already exists, just load embeds
      window.Tally.loadEmbeds();
    }
    
    return () => {
      // Clean up script when component unmounts
      const script = document.getElementById('tally-script');
      if (script) {
        document.body.removeChild(script);
      }
    };
  }, [jobId, navigate]);
  
  // Handle when form iframe is loaded
  const handleIframeLoad = () => {
    setFormLoading(false);
  };
  
  if (!job) {
    return (
      <Container className="text-center py-5" style={{ marginTop: '100px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }
  
  return (
    <div className="modern-ai-theme">
      {/* Modern grid background with lines */}
      <div className="grid-lines" style={{ zIndex: 0 }}></div>
      
      {showForm ? (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          zIndex: 1200, // Higher than navbar
          background: 'var(--ai-white)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{ 
            padding: '15px', 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            zIndex: 1201
          }}>
            <h4 style={{ margin: 0 }}>Apply: {job.title}</h4>
            <Button 
              variant="outline-light" 
              size="sm" 
              onClick={() => setShowForm(false)}
              style={{ position: 'relative', zIndex: 1210 }}
            >
              Back to Job Details
            </Button>
          </div>
          
          <div style={{ position: 'relative', flexGrow: 1 }}>
            {formLoading && (
              <div style={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                background: 'var(--ai-white)',
                zIndex: 1202
              }}>
                <Spinner animation="border" variant="light" />
              </div>
            )}
            <iframe 
              data-tally-src={`${job.formUrl}?transparentBackground=1`} 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              marginHeight="0" 
              marginWidth="0" 
              title={job.title}
              onLoad={handleIframeLoad}
              style={{ border: 'none', zIndex: 1201 }}
            />
          </div>
        </div>
      ) : (
        <>
          {/* Job detail section */}
          <section className="py-5" style={{ 
            paddingTop: '120px',
            background: 'var(--ai-white)',
            position: 'relative',
            zIndex: 1
          }}>
            <Container>
              <Row className="mb-4">
                <Col>
                  <Link 
                    to="/careers" 
                    className="text-light text-decoration-none mb-4 d-inline-block"
                    style={{ position: 'relative', zIndex: 5 }}
                  >
                    <i className="bi bi-arrow-left me-2"></i> Back to Careers
                  </Link>
                </Col>
              </Row>
              
              {/* Embedded Tally Form */}
              <Row>
                <Col>
                  <div 
                    className="glass-card mb-4 form-container" 
                    style={{ 
                      position: 'relative',
                      zIndex: 2,
                      minHeight: '700px',
                      padding: 0,
                      overflow: 'hidden'
                    }}
                    ref={formRef}
                  >
                    {formLoading && (
                      <div style={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        bottom: 0, 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        background: 'rgba(0,0,0,0.6)',
                        backdropFilter: 'blur(5px)',
                        zIndex: 3
                      }}>
                        <Spinner animation="border" variant="light" />
                      </div>
                    )}
                    <iframe 
                      data-tally-src={`${job.formUrl}?transparentBackground=1`} 
                      width="100%" 
                      height="100%" 
                      frameBorder="0" 
                      marginHeight="0" 
                      marginWidth="0" 
                      title={job.title}
                      onLoad={handleIframeLoad}
                      style={{ 
                        border: 'none', 
                        zIndex: 2,
                        minHeight: '700px',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0
                      }}
                    />
                  </div>
                </Col>
              </Row>
            </Container>
          </section>
          
          {/* Footer */}
          <Footer />
        </>
      )}
    </div>
  );
};

export default JobDetail; 